import React, { useMemo } from "react"
import SbEditable from "storyblok-react"
import tw from "twin.macro"
import { PartnerGridStoryblok, PartnerPageStoryblok } from "../../../component-types-sb"
import useLocale from "../../hooks/useLocale"
import Container from "../general/Container"
import { H2, Subtitle } from "../typography/Typography"
import usePartnerEntries from "../../hooks/usePartnerEntries"
import PartnerCard from "../cards/PartnerCard"
import { getCityProvinceCountry } from "../../utils/locationHelper"
import { getStudioTypeNames, getExtra } from "../../utils/partnerHelpers"
import usePartnerAttributes from "../../hooks/usePartnerAttributes"
import Search from "../blogComponents/Search"
import { PartnerFilterProvider, usePartnerFilterContext } from "../../contexts/PartnerFilterContext"
import { matchesExtraFilters } from "../../utils/matchesExtraFilters"
import { matchesLocationFilter } from "../../utils/matchesLocationFilter"
import { matchesStudioAmountFilter } from "../../utils/matchesStudioAmountFilter"
import { matchesTypeFilter } from "../../utils/matchesTypeFilter"
import PartnerTagFilter from "./PartnerTagFilter"
import { CardGrid, NoResultsWrapper, Wrapper } from "./BlogTypesGrid"

interface Props {
  readonly blok: PartnerGridStoryblok
}

const FilterWrapper = tw.div`
  bg-blue-sky
  p-4 pt-1 mt-4
  rounded-lg shadow-lg
  flex flex-col
  gap-6
  laptop:(sticky h-fit)
`

const PartnerGrid: React.FC<Props> = ({ blok }) => {
  const { locale } = useLocale()
  const { allTypes, allExtras, cityMap, provinceMap, countryMap } = usePartnerAttributes(locale)
  const partners = usePartnerEntries()
  const localizedPartners = useMemo(
    () => (partners || []).filter((partner) => partner.lang === locale),
    [partners, locale]
  )
  const { selectedFilters } = usePartnerFilterContext()

  // Filtering logic for partners
const filteredPartners = useMemo(() =>
  localizedPartners.filter((entry) => {
    const content = JSON.parse(entry.content) as PartnerPageStoryblok
    const locationData = Array.isArray(content.location)
      ? content.location.flatMap((loc) => getCityProvinceCountry(loc, {cityMap, countryMap, provinceMap}))
      : getCityProvinceCountry(content.location ?? "", {cityMap, countryMap, provinceMap})

    const type = getStudioTypeNames(content.studio_type, allTypes)
    const extra = getExtra(content.extra ?? [], allExtras)

    if (!matchesLocationFilter(locationData, selectedFilters)) return false
    if (!matchesStudioAmountFilter(content.studio_amount, selectedFilters.studioAmount)) return false
    if (!matchesTypeFilter(type, selectedFilters.type)) return false
    if (!matchesExtraFilters(extra, selectedFilters.extra)) return false

    return true
  }),
[localizedPartners, selectedFilters, cityMap, provinceMap, countryMap, allTypes, allExtras])

  return (
    <SbEditable content={blok}>
      <Container>
        <Wrapper>
          {/* Filter for desktop, outside of CardGrid */}
          <FilterWrapper tw="hidden laptop:block w-[250px]">
            <div>
              <Search isPartnerPage={true} sections={["partnerPage"]} />
            </div>
            <PartnerTagFilter blok={blok} />
          </FilterWrapper>

          <CardGrid>
            {/* Filter for mobile devices, as part of CardGrid */}
            <FilterWrapper tw="block laptop:hidden">
              <Search
                isPartnerPage={true}
                sections={["partnerPage"]}
                showSearchTextOnMobile={true}
                buttonStyling={tw`w-full`}
              />
              <PartnerTagFilter blok={blok} />
            </FilterWrapper>

            {filteredPartners.length > 0 ?
              filteredPartners.map((partner) => {
                const content = JSON.parse(partner.content) as PartnerPageStoryblok
                return (
                  <PartnerCard
                    key={partner.uuid}
                    content={content}
                    full_slug={partner.full_slug}
                  />
                )
              })
            :
              <NoResultsWrapper>
                <H2>{blok.no_result_title}</H2>
                <Subtitle>{blok.no_result_subtitle}</Subtitle>
              </NoResultsWrapper>
            }
          </CardGrid>
        </Wrapper>
      </Container>
    </SbEditable>
  )
}

const PartnerGridWrapper: React.FC<Props> = ({ blok }) => (
  <PartnerFilterProvider>
    <PartnerGrid blok={blok} />
  </PartnerFilterProvider>
)

export default PartnerGridWrapper
